/**
 * photo-editor.js
 */

import React from 'react';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  Typography,
  Button as MuiButton,
  Slider,
} from '@mui/material';
import Link from '@mui/material/Link';
import AvatarEditor from 'react-avatar-editor'
import Dropzone from 'react-dropzone'
import debounce from 'lodash.debounce';
import { isUsingTouchInput } from 'utils/app-context';

const useStyles = makeStyles(theme => ({
  form: {
    width: theme.size.avatarSquare,
    height: theme.size.avatarSquare,
  },
  container: {
    minHeight: '200px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '70%',
      maxHeight: '70%',
    },
    outline: 'none',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey.colors[9],
    border: `dashed 4px ${theme.palette.grey.colors[7]}`,
    borderRadius: '5px',
    margin: '0 auto',
    cursor: 'grab',
  },
  containerDragOver: {
    opacity: 0.5,
    borderColor: theme.palette.primary.main,
  },
  link: {
    fontWeight: 500,
    cursor: 'pointer'
  },
  instructionText: {
    flex: 1,
    textAlign: 'center'
  }, 
  editor: {
    height: '100% !important',
    width: '100% !important',
  },
  rotateButton: {
    flex: 1,
  }
}));

const PhotoEditor = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const [dragOver, setDragOver] = React.useState(false);
  const [photo, setPhoto] = React.useState();
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const editorRef = React.useRef();
  const dirtyDebounce = React.useRef(debounce((ref) => {
    if (ref?.current) {
      props.onChange(ref.current);
    }
  }, 100)).current;

  const setPhotoUrl = (photoUrlString) => {
    if (photoUrlString) {
      try {
        const url = new URL(photoUrlString);
        // If this is a complete URL, add a random param to workaround S3/chromium issue. 
        // See https://www.hacksoft.io/blog/handle-images-cors-error-in-chrome
        url.searchParams.append('nc', '1');
        setPhoto(url.toString());
      } catch {
        // Not an complete URL
        setPhoto(photoUrlString);
      }
    } else {
      setPhoto(null);
    }
  };

  React.useEffect(() => {
    if (!photo) {
      setPhotoUrl(props.photo);
      setZoom(1);
      setRotation(0);
    }
  }, [props.photo, photo]);

  const click = (e) => {
    if (photo) {
      e.stopPropagation();
    }
  }

  const handleFiles = (photos) => {
    setDragOver(false);
    setZoom(1);
    setRotation(0);
    setPhotoUrl(photos[0]);
    // Wait for next render cycle when editor is ready to avoid infinite loop
    setTimeout(() => {
      if (editorRef.current) {
        dirtyDebounce({ current: editorRef.current });
      }
    }, 0);
  }

  return (
    <Box className={classes.form}>
      <Dropzone 
        onDrop={handleFiles} 
        onDragEnter={() => setDragOver(true)}
        onDragLeave={() => setDragOver(false)}
      >
        {({ getRootProps, getInputProps }) => {
          const {ref, ...customRootProps} = getRootProps();
          return <>
            <Box 
              className={classes.width} 
              display="flex"
              mb={2}
            >
              <Typography
                variant="body2"
                className={classes.instructionText}
              >
                {!isUsingTouchInput() && <><span>Drag and drop an image below or</span><br /></>}
                <Link className={classes.link} {...customRootProps} underline="hover">
                  SELECT IMAGE
                </Link>
                {' '} to upload
              </Typography>


            </Box>
            <Box 
              className={
                `flex-hc `
                + `${classes.width} `
                + `${classes.container} `
                + `${dragOver ? classes.containerDragOver : null}`}
              {...getRootProps({
                onClick: e => click(e)
              })} 
            >
              {photo && 
              <AvatarEditor
                ref={editorRef}
                className={classes.editor}
                borderRadius={theme.size.avatarSquare}
                color={[255, 255, 255, 0]}
                border={0}
                scale={zoom}
                rotate={rotation}
                crossOrigin="anonymous"
                image={photo} 
                onImageChange={() => dirtyDebounce({ current: editorRef.current })}
              />}
              {!photo && 
              <Box>
                <Typography variant="h6" align="center">
                  {isUsingTouchInput() ? "Tap To Select Image" : "Drop Image Here"}
                </Typography>
              </Box>}
              <input {...getInputProps()} />
            </Box>
          </>;
        }}
      </Dropzone>
      <Box mt={2}>
        <Typography id="continuous-slider">
          Zoom
        </Typography>
        <Slider 
          value={zoom} 
          onChange={(e, newVal) => setZoom(newVal)} 
          min={0.2}
          max={5}
          step={0.01}
          aria-labelledby="zoom level" 
          disabled={!photo}
        />
        <Box display="flex">
          <MuiButton 
            className={classes.rotateButton} 
            onClick={() => setRotation((rotation - 90) % 360)}
            disabled={!photo}
          >
            Rotate Left
          </MuiButton>
          <MuiButton 
            className={classes.rotateButton} 
            onClick={() => setRotation((rotation + 90) % 360)}
            disabled={!photo}
          >
            Rotate Right
          </MuiButton>
        </Box>
      </Box>
    </Box>
  );
}

export default PhotoEditor;